import axios from 'axios';
import { toast } from 'react-toastify';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_GETWAY_API+ "dif_recon/api/v1" || 'http://localhost:8626/dif_recon/api/v1';

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        // Show success toast for non-GET requests (mutations)
        // Skip if skipToast is explicitly set to true in the request config
        if (response.config.method !== 'get' && response.data?.message && !response.config.skipToast) {
            toast.success(response.data.message);
        } else if (response.config.method !== 'get' && response.status === 200) {
            // Some APIs might not return a message, but we still want to indicate success for mutations
            // toast.success('Action completed successfully'); 
        }
        return response.data;
    },
    (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        
        if (error.response?.status === 401) {
            // Token expired or invalid
            const isLoginPage = window.location.pathname === '/';
            if (!isLoginPage) {
                toast.error('Session expired. Please login again.');
                localStorage.clear();
                window.location.href = '/';
            }
        } else {
            toast.error(message);
        }
        
        return Promise.reject(error);
    }
);

const MasterService = {
    post: (url, data, config = {}) => {
        return apiClient.post(url, data, config);
    },

    get: (url, config = {}) => {
        return apiClient.get(url, config);
    },

    put: (url, data, config = {}) => {
        return apiClient.put(url, data, config);
    },

    delete: (url, config = {}) => {
        return apiClient.delete(url, config);
    },

    // Specialized methods for easy reuse
    login: (data) => {
        return MasterService.post('/auth/login', data);
    },

    logout: (userId) => {
        return MasterService.post('/auth/logout', { userId });
    },

    getReconciliationList: (filters) => {
        return MasterService.post('/reconciliation/list-all', filters, { skipToast: true });
    },

    getReconciliationDetails: (uploadProofId) => {
        return MasterService.post('/reconciliation/get_upload_proof_match_details', {
            upload_proof_id: uploadProofId
        }, { skipToast: true });
    },

    editBankMatch: (bankStatementId, uploadProofId, statementItem) => {
        return MasterService.post('/reconciliation/edit_upload_proof_bank_match', {
            bank_statement_id: bankStatementId,
            upload_proof_id: uploadProofId,
            statement_item: statementItem
        });
    },

    editLoanMatch: (payload) => {
        return MasterService.post('/reconciliation/edit_upload_proof_loan_match', payload);
    },

    deleteMatch: (uploadProofId, matchType) => {
        return MasterService.post('/reconciliation/delete_match', {
            upload_proof_id: uploadProofId,
            match_type: matchType
        });
    },

    reconcileUploadProofMatch: (uploadProofIds) => {
        return MasterService.post('/reconciliation/reconcile_ids', {
            upload_proof_ids: uploadProofIds
        }).then(response => {
            if(response?.bankStatementResults?.length > 0 && response?.bankStatementResults[0]?.status == 400){
                toast.error('Reconciliation failed');
            }else{
                const proofId = Array.isArray(uploadProofIds) ? uploadProofIds[0] : uploadProofIds;
                toast.success(`Reconciliation completed successfully for Proof ID: ${proofId}`, {
                    autoClose: 5000
                });
            }
            return response;
        });
    },

    getLeadList: (dealerId, searchText) => {
        return MasterService.post('/reconciliation/get_lead_list', {
            dealer_id: dealerId,
            search_text: searchText
        }, { skipToast: true });
    },

    getStatusHistory: (uploadProofId) => {
        return MasterService.post('/reconciliation/get_status_history', {
            upload_proof_id: uploadProofId
        }, { skipToast: true });
    },

    getStatementList: (statementDate) => {
        return MasterService.post('/reconciliation/get_statement_list', {
            statement_date: statementDate
        }, { skipToast: true });
    }
};

export default MasterService;
